/**
 * Single source of truth for Award Party brand colors.
 * Used by apps/web (CSS variables + Tailwind). Native shells via Capacitor
 * reuse the same web UI, so keep brand tokens here.
 *
 * Web UI primarily uses `--mk-*` tokens in globals.css (same hex values).
 */
export const brandTheme = {
  light: {
    /** Page/screen background (cream) */
    background: "#faf6ee",
    /** Primary text (ink) */
    text: "#0e0c0a",
    /** Primary CTA / links / tint (gold) */
    primary: "#c9a84c",
    /** Text on primary buttons */
    primaryForeground: "#0e0c0a",
    /** Cards, modals */
    card: "#ffffff",
    /** Card/section borders */
    cardBorder: "rgba(14, 12, 10, 0.12)",
    /** Secondary/muted text */
    secondaryText: "rgba(14, 12, 10, 0.55)",

    /** Party list card border */
    partyCardBorder: "rgba(201, 168, 76, 0.45)",
    /** Owner badge background */
    ownerBadgeBg: "rgba(201, 168, 76, 0.12)",
    /** Owner badge text */
    ownerBadgeText: "#8a7030",
    /** Locked status badge bg */
    lockedBadgeBg: "#ffe4e6",
    /** Locked status text */
    lockedBadgeText: "#be123c",
    /** Open status badge bg */
    openBadgeBg: "#dcfce7",
    /** Open status text */
    openBadgeText: "#15803d",
    /** Pending invitation card bg */
    invitationCardBg: "rgba(201, 168, 76, 0.08)",
    /** Pending invitation border */
    invitationCardBorder: "rgba(201, 168, 76, 0.45)",
    /** Muted meta text */
    metaText: "rgba(14, 12, 10, 0.55)",
    /** Error/destructive text */
    errorText: "#b91c1c",
  },
  dark: {
    /** Page/screen background (ink) */
    background: "#0e0c0a",
    /** Primary text (cream) */
    text: "#faf6ee",
    /** Primary CTA / links / tint (gold) */
    primary: "#c9a84c",
    /** Text on primary buttons */
    primaryForeground: "#0e0c0a",
    /** Cards, modals (ink soft) */
    card: "#1c1914",
    /** Card/section borders */
    cardBorder: "rgba(250, 246, 238, 0.1)",
    /** Secondary/muted text */
    secondaryText: "rgba(250, 246, 238, 0.35)",

    partyCardBorder: "rgba(250, 246, 238, 0.1)",
    ownerBadgeBg: "rgba(201, 168, 76, 0.12)",
    ownerBadgeText: "#e8c96a",
    lockedBadgeBg: "#451a03",
    lockedBadgeText: "#fecdd3",
    openBadgeBg: "#14532d",
    openBadgeText: "#bbf7d0",
    invitationCardBg: "rgba(28, 25, 20, 0.5)",
    invitationCardBorder: "rgba(250, 246, 238, 0.1)",
    metaText: "rgba(250, 246, 238, 0.4)",
    errorText: "#fecdd3",
  },
} as const;

export type BrandTheme = typeof brandTheme;
export type BrandThemeMode = keyof BrandTheme;
